import { Link, useLocation } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { useAuth } from "../../contexts/AuthContext";
import { careService } from "../../services/careService";
import {
  ncpAssessmentFields,
  ncpExistingClientSteps,
  ncpNewClientSteps,
  ncpObservationNotes,
  ncpScreens,
} from "./careData";

const screenDetails = (screen) => {
  const [eyebrow, title, description, status, cta, ctaPath] = ncpScreens[screen] || ncpScreens.overview;
  return { eyebrow, title, description, status, cta, ctaPath };
};

const stepTone = (state) => (state === "active" ? "blue" : state === "review" ? "purple" : "low");

const formatDate = (value) => {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not set";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const label = (value = "") =>
  String(value || "")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const dateValue = (value) => {
  if (!value) return Number.POSITIVE_INFINITY;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? Number.POSITIVE_INFINITY : date.getTime();
};

const planPriority = (plan) => {
  if (!plan?.reviewDueDate) return "No review date";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(plan.reviewDueDate);
  due.setHours(0, 0, 0, 0);
  const diffDays = Math.round((due.getTime() - today.getTime()) / 86400000);
  if (diffDays < 0) return "Overdue";
  if (diffDays === 0) return "Due today";
  if (diffDays <= 14) return "Due soon";
  return "Current";
};

const priorityTone = (priority) =>
  priority === "Overdue" ? "high" : priority === "Due today" || priority === "Due soon" ? "medium" : "blue";

const parseAttachedFiles = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed;
  } catch (error) {
    // Some existing records store this as a comma/newline separated string.
  }
  return String(value)
    .split(/[\n,]+/)
    .map((item) => item.trim())
    .filter(Boolean);
};

const sourceList = (plans = []) =>
  Array.from(new Set(plans.flatMap((plan) => String(plan.dataSources || "")
    .split(/[\n,]+/)
    .map((item) => item.trim())
    .filter(Boolean))));

export default function NegotiatedCarePlanPage({ screen = "overview" }) {
  const { user } = useAuth();
  const actorId = user?.id || user?.userId || user?.uid || "";
  const details = screenDetails(screen);
  const { pathname } = useLocation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    careService.negotiatedCarePlans.list({}, actorId)
      .then((data) => {
        if (!active) return;
        setPlans(Array.isArray(data) ? data : []);
        setError("");
      })
      .catch((err) => {
        if (!active) return;
        setError(err?.response?.data?.message || err?.message || "Unable to load negotiated care plans.");
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [actorId]);

  const activePlans = useMemo(() => plans.filter((plan) => !["ARCHIVED", "SUPERSEDED"].includes(plan.status)), [plans]);
  const dueSoon = useMemo(() => activePlans.filter((plan) => ["Overdue", "Due today", "Due soon"].includes(planPriority(plan))), [activePlans]);
  const confirmed = useMemo(() => activePlans.filter((plan) => plan.status === "CONFIRMED_BY_STATE"), [activePlans]);
  const newestUpdated = useMemo(() => [...plans].sort((a, b) => dateValue(b.updatedAt || b.createdAt) - dateValue(a.updatedAt || a.createdAt))[0], [plans]);
  const selectedPlan = useMemo(() => {
    if (screen === "confirmed") return confirmed[0] || newestUpdated;
    return dueSoon[0] || activePlans[0] || newestUpdated;
  }, [activePlans, confirmed, dueSoon, newestUpdated, screen]);
  const attachedFiles = parseAttachedFiles(selectedPlan?.attachedFiles);
  const dataSources = sourceList(activePlans);
  const heroStatus = loading
    ? "Loading plans"
    : `${activePlans.length} active ${activePlans.length === 1 ? "plan" : "plans"}`;

  return (
    <CareShell className="dashboard-content ncp-flow">
      <div className="crumbs">Care Plans <i className="ti ti-chevron-right" /> Negotiated Care Plan</div>
      <div className="ncp-hero">
        <div>
          <span className="section-eyebrow">{details.eyebrow}</span>
          <h1>{details.title}</h1>
          <p>{details.description}</p>
        </div>
        <div className="ncp-hero-actions">
          <em>{heroStatus}</em>
          <Link className="btn primary" to="/assessments">Open Care Processes</Link>
        </div>
      </div>

      {error ? <p className="form-error">{error}</p> : null}

      <div className="ncp-status-grid">
        {[
          ["ti-heart-handshake", "Active NCPs", "Negotiated care plans in this workspace", activePlans.length],
          ["ti-calendar-due", "Due soon", "Review due, due today, or overdue", dueSoon.length],
          ["ti-file-check", "State confirmed", "Plans confirmed by state", confirmed.length],
          ["ti-users", "Residents covered", "Residents with a negotiated care plan", new Set(plans.map((plan) => plan.residentId).filter(Boolean)).size],
        ].map(([icon, label, text, status]) => (
          <article className="ncp-status-card" key={label}>
            <span className="tiny-icon"><i className={`ti ${icon}`} /></span>
            <div><h3>{label}</h3><p>{text}</p></div>
            <em>{status}</em>
          </article>
        ))}
      </div>

      <Panel title="Negotiated Care Plans">
        {loading ? (
          <div className="ncp-empty-state">Loading negotiated care plans...</div>
        ) : !plans.length ? (
          <div className="ncp-empty-state">
            <strong>No negotiated care plans found.</strong>
            <span>Complete an assessment negotiated care plan stage to create records for this workspace.</span>
          </div>
        ) : (
          <div className="ncp-plan-table">
            {plans.map((plan) => {
              const priority = planPriority(plan);
              return (
                <Link className="ncp-plan-row" to={plan.residentId ? `/residents/${plan.residentId}#care-plans` : "#"} key={plan.id}>
                  <span className="care-dashboard-avatar blue">{plan.residentInitials || "--"}</span>
                  <span className="ncp-plan-main">
                    <strong>{plan.residentName || "Unnamed resident"}</strong>
                    <small>{plan.roomNumber ? `Room ${plan.roomNumber}` : label(plan.residentStatus)}</small>
                  </span>
                  <span>
                    <b>{plan.title || "Negotiated care plan"}</b>
                    <small>{plan.templateName || "Template not set"}</small>
                  </span>
                  <span>
                    <b>{label(plan.status)}</b>
                    <small>Review {formatDate(plan.reviewDueDate)}</small>
                  </span>
                  <em className={`pill ${priorityTone(priority)}`}>{priority}</em>
                </Link>
              );
            })}
          </div>
        )}
      </Panel>

      <div className="ncp-screen-grid">
        <Panel title="New Client Flow">
          <div className="ncp-step-list">
            {ncpNewClientSteps.map(([number, title, text, path, state]) => (
              <Link className={`ncp-step ${path === pathname ? "is-active" : ""}`} to={path} key={`${number}-${title}`}>
                <b>{number}</b><span><strong>{title}</strong><small>{text}</small></span><em className={`pill ${stepTone(state)}`}>{state}</em>
              </Link>
            ))}
          </div>
        </Panel>
        <Panel title="Existing Client Flow">
          <div className="ncp-step-list compact">
            {ncpExistingClientSteps.map(([number, title, text, path, state]) => (
              <Link className={`ncp-step ${path === pathname ? "is-active" : ""}`} to={path} key={`${number}-${title}`}>
                <b>{number}</b><span><strong>{title}</strong><small>{text}</small></span><em className={`pill ${stepTone(state)}`}>{state}</em>
              </Link>
            ))}
          </div>
        </Panel>
      </div>

      <div className="ncp-detail-grid">
        <Panel title={screen === "annual" ? "Yearly Assessment Inputs" : "Assessment and MaG Inputs"}>
          <div className="ncp-field-grid">
            {(dataSources.length ? dataSources.slice(0, 4).map((source) => ["ti-database", source, "Linked to negotiated care plan records in this workspace."]) : ncpAssessmentFields).map(([icon, fieldLabel, text]) => (
              <article className="ncp-field-card" key={fieldLabel}>
                <span className="round-icon blue"><i className={`ti ${icon}`} /></span>
                <h3>{fieldLabel}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </Panel>

        <Panel title={screen === "confirmed" ? "Confirmed Files" : "Care Plan Workspace"}>
          {screen === "confirmed" || attachedFiles.length ? (
            <div className="ncp-file-list">
              {attachedFiles.length ? attachedFiles.map((file, index) => {
                const fileName = typeof file === "string" ? file : file.name || file.fileName || file.originalFileName || `Attached file ${index + 1}`;
                const url = typeof file === "string" ? "" : file.signedUrl || file.url || file.downloadUrl || "";
                return (
                  <a className="ncp-file-row" href={url || "#download"} key={`${fileName}-${index}`} target={url ? "_blank" : undefined} rel="noreferrer">
                    <i className="ti ti-file-download" /><span>{fileName}</span><strong>{url ? "Open" : "Linked"}</strong>
                  </a>
                );
              }) : <div className="ncp-empty-state">No confirmed files are attached to this plan yet.</div>}
            </div>
          ) : (
            <div className="ncp-workspace-card">
              <div className="ncp-template-switch">
                <span className="mini-btn edit">{selectedPlan?.templateName || "Template not set"}</span>
                <span className="mini-btn">{label(selectedPlan?.signatureStatus || "NOT_REQUIRED")}</span>
              </div>
              <label>
                Current plan summary
                <textarea readOnly value={selectedPlan?.title || "Select or create a negotiated care plan from a resident care process."} />
              </label>
              <div className="ncp-negotiation-row">
                <span>
                  <strong>{selectedPlan?.residentName || "No resident selected"}</strong>
                  <small>Status {label(selectedPlan?.status)} • Review {formatDate(selectedPlan?.reviewDueDate)}</small>
                </span>
                {selectedPlan?.residentId ? <Link className="mini-btn" to={`/residents/${selectedPlan.residentId}#care-plans`}><i className="ti ti-eye" /> Open</Link> : null}
              </div>
            </div>
          )}
        </Panel>
      </div>

      <Panel title={screen === "probation" ? "30 Days Probation" : "Ongoing Notes and Care Team Updates"}>
        <div className="ncp-note-grid">
          {activePlans.slice(0, 4).length ? activePlans.slice(0, 4).map((plan) => (
            <article className="ncp-note-card" key={plan.id}>
              <strong>{plan.residentName || "Unnamed resident"}</strong>
              <p>{plan.title || "Negotiated care plan"} • {label(plan.status)} • Review {formatDate(plan.reviewDueDate)}</p>
            </article>
          )) : ncpObservationNotes.map(([noteLabel, text]) => <article className="ncp-note-card" key={noteLabel}><strong>{noteLabel}</strong><p>{text}</p></article>)}
        </div>
        <div className="ncp-team-row">
          {["Assessment", "Resident record", "Daily notes", "Final assessment", "State confirmation"].map((member) => <span key={member}><i className="ti ti-user-check" /> {member}</span>)}
        </div>
      </Panel>
    </CareShell>
  );
}
